"use client";

import { useEffect } from "react";
import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  searchCoverage,
  autocomplete,
  useGetProject,
  listSavedSearches,
  getSavedSearch,
  createSavedSearch,
  updateSavedSearch,
} from "@/lib/api/generated";
import {
  getProfileWithContext,
  type ProfileViewContext,
} from "@/hooks/use-profile-detail";
import { useAuth } from "@/features/auth";
import { useOptionalTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { customInstance } from "@/lib/api/client";
import { useSearchRunsStore } from "@/stores/search-runs-store";
import type {
  SearchRequestSchema,
  SearchFiltersSchema,
  SearchProfilesResponseSchema,
  AutocompleteItemSchema,
  ProfileDetailSchema,
  ProjectResponse,
  SavedSearchSchema,
  SavedSearchListResponseSchema,
  SearchWithRunResponseSchema,
  SavedSearchUpdateSchema,
  ExportHistoryItemSchema,
  ExportHistoryListSchema,
  ExportEstimateSchema,
} from "@/lib/api/model";
import type { InsightsData } from "@/lib/api/types";
import type { ShareInsightSectionKey } from "@/features/search/components/share/lib/config";

export function projectSearchesQueryKey(projectId: string | null) {
  return ["project-searches", projectId] as const;
}

/**
 * Prefix shared by every saved-search payload key; invalidating it refreshes
 * all of them (the run stream does this on reconnect).
 */
export const SAVED_SEARCH_QUERY_PREFIX = ["saved-search"] as const;

/**
 * Cache key for `GET /searches/{id}`. The saved-search payload is not
 * paginated (results live under `searchResultsQueryKey`), so this is the one
 * key `useSavedSearch`, `useRunSearch`, the assistant and the SSE hook's
 * invalidation all agree on.
 */
export function savedSearchQueryKey(searchId: string | null) {
  return [...SAVED_SEARCH_QUERY_PREFIX, searchId] as const;
}

export function useCoverage(
  filters: SearchFiltersSchema,
  poolIds: string[] | null = null,
  enabled = true,
) {
  return useQuery({
    queryKey: ["search-coverage", filters, poolIds],
    queryFn: () => searchCoverage({ filters, pool_ids: poolIds }),
    enabled,
    staleTime: 30_000,
  });
}

export function useSelectedProject() {
  const { selectedProjectId } = useAuth();
  const query = useGetProject(selectedProjectId ?? "", {
    query: {
      enabled: !!selectedProjectId,
      staleTime: 30_000,
    },
  });
  return {
    ...query,
    data: selectedProjectId ? query.data ?? null : null,
  };
}

export function useSavedSearchesList(
  projectId: string | null,
  params?: { page?: number; per_page?: number },
) {
  const query = useQuery({
    queryKey: [...projectSearchesQueryKey(projectId), params],
    queryFn: async () => {
      if (!projectId)
        return {
          searches: [],
          total: 0,
          page: 1,
          per_page: params?.per_page ?? 10,
        } as SavedSearchListResponseSchema;
      return listSavedSearches(projectId, params);
    },
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });

  // Every list payload carries each search's `latest_run`; feed it to the
  // runs store so sidebar / list indicators converge with the SSE stream.
  const searches = query.data?.searches;
  useEffect(() => {
    if (searches?.length) {
      useSearchRunsStore.getState().hydrateList(searches);
    }
  }, [searches]);

  return query;
}

/**
 * Poll cadence for `useSavedSearch`: a fixed interval, `false`, or a function
 * of the payload currently cached for this search (never the placeholder
 * carried over from another search), re-evaluated by the query client.
 */
export type SavedSearchRefetchInterval =
  | number
  | false
  | ((data: SearchWithRunResponseSchema | undefined) => number | false);

/**
 * `GET /searches/{id}` → `{ search, latest_run }`. Hydrates the runs store
 * from `latest_run` on every payload so a fresh page load lands on the right
 * phase before the SSE stream has delivered anything.
 *
 * `refetchInterval` lets the results provider poll the payload as a fallback
 * while a run is in flight and no live run stream covers the search's
 * project; the function form lets it read `search.project_id` off the payload.
 */
export function useSavedSearch(
  searchId: string | null,
  {
    refetchInterval = false,
  }: { refetchInterval?: SavedSearchRefetchInterval } = {},
) {
  const query = useQuery({
    queryKey: savedSearchQueryKey(searchId),
    queryFn: () => getSavedSearch(searchId!),
    enabled: !!searchId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval:
      typeof refetchInterval === "function"
        ? (q) => refetchInterval(q.state.data)
        : refetchInterval,
  });

  const data = query.data;
  useEffect(() => {
    if (!data) return;
    useSearchRunsStore.getState().hydrateSearch({
      ...data.search,
      latest_run: data.latest_run ?? null,
    });
  }, [data]);

  return query;
}

export function useCreateSavedSearch() {
  return useMutation({
    mutationFn: ({
      projectId,
      request,
    }: {
      projectId: string;
      request: SearchRequestSchema;
    }) => createSavedSearch(projectId, request),
  });
}

export function useUpdateSavedSearch(searchId: string | null) {
  return useMutation({
    mutationFn: (body: SavedSearchUpdateSchema) =>
      updateSavedSearch(searchId!, body),
  });
}

type AutocompleteField =
  | "skills"
  | "companies"
  | "locations"
  | "job_titles"
  | "industries"
  | "universities"
  | "company_tags"
  | "investors"
  | "majors"
  | "languages";

export function useAutocomplete(
  field: string,
  prefix: string,
  enabled = true,
  allowEmpty = false,
) {
  const limit = allowEmpty && !prefix ? 200 : 10;
  // Inside a search session, suggest values from the search's own pools.
  const poolIds = useOptionalTalentSearchFilters()?.poolIds ?? null;
  return useQuery({
    queryKey: ["autocomplete", field, prefix, limit, poolIds],
    queryFn: async () => {
      const data = await autocomplete(field as AutocompleteField, {
        prefix,
        limit,
        ...(poolIds?.length ? { pool_ids: poolIds } : {}),
      });
      return data.results;
    },
    enabled: enabled && (allowEmpty || prefix.length >= 1),
    staleTime: 60_000,
  });
}

export function useCandidateProfile(
  candidateId: string | null,
  context?: ProfileViewContext,
) {
  return useQuery({
    queryKey: ["candidate", candidateId, context?.searchId ?? null, context?.projectId ?? null],
    queryFn: () => getProfileWithContext(candidateId!, context),
    enabled: !!candidateId,
  });
}

export function useSearchInsights(
  searchId: string | null,
  request: SearchRequestSchema,
  enabled = true,
) {
  return useQuery<InsightsData>({
    queryKey: ["search-insights", searchId, request.filters, request.pool_ids],
    queryFn: () =>
      customInstance<InsightsData>(`/api/searches/${searchId}/insights`, {
        method: "POST",
        body: JSON.stringify(request),
      }),
    enabled: enabled && !!searchId,
    staleTime: 60_000,
  });
}

export type {
  SearchRequestSchema,
  SearchFiltersSchema,
  SearchProfilesResponseSchema,
  AutocompleteItemSchema,
  ProfileDetailSchema,
  ProjectResponse,
  SavedSearchSchema,
  SearchWithRunResponseSchema,
  SavedSearchListResponseSchema,
};

// Re-export the orval-generated BulkSelection so call-sites have a single
// source of truth that stays in sync with the backend OpenAPI schema.
export type { BulkSelection } from "@/lib/api/model/bulkSelection";
import type { BulkSelection } from "@/lib/api/model/bulkSelection";
import type { PipelineFilterSchema } from "@/lib/api/model/pipelineFilterSchema";

export type ExportFormat = "pdf" | "csv" | "csv+pdf";

/**
 * Export source — discriminated union. Add new source types here as we add
 * new export entry points; the backend's `ExportService` stays the single
 * source of truth.
 */
export type ExportSource =
  | {
      type: "search";
      search_id: string;
      bulk: BulkSelection;
    }
  | {
      type: "pipeline";
      job_id: string;
      job_candidate_ids?: string[];
      bulk?: BulkSelection;
      stage_id?: string;
      search?: string;
      /** Filter-panel selections, so a `bulk: all` export matches the table. */
      filters?: PipelineFilterSchema;
    }
  | {
      type: "arbitrator_session";
      session_id: string;
      bulk: BulkSelection;
      min_score?: number | null;
    }
  | {
      type: "arbitrator_evaluation";
      session_id: string;
      profile_id: string;
    }
  | {
      type: "search_insights";
      search_id: string;
    };

/** Sources that export candidate profiles (everything but the Insights report). */
export type ProfileExportSource = Exclude<
  ExportSource,
  { type: "search_insights" }
>;

export interface ExportOptions {
  name_redaction?: "none" | "initials_only" | "anonymized";
  hide_exporter?: boolean;
  verified_contacts_only?: boolean;
  personal_emails_only?: boolean;
  personal_emails_preferred?: boolean;
  include_highlights?: boolean;
  csv_columns?: string[] | null;
  theme?: "dark" | "light";
}

export interface StartExportParams {
  source: ExportSource;
  format?: ExportFormat;
  include_contacts?: boolean;
  options?: ExportOptions;
  /** `search_insights` only: sections left out of the report. */
  hidden_insight_sections?: ShareInsightSectionKey[];
  /** Credit total the user confirmed; the backend 409s if the price rose. */
  expected_credits?: number;
}

export interface InsightsFormatPricing {
  format: "pdf" | "csv";
  credits: number;
}

export interface InsightsExportEstimate {
  credits_per_format: number;
  formats: InsightsFormatPricing[];
  available_sections: ShareInsightSectionKey[];
  /** The org already has a delivered export of this search: every later
   * export of it is free, whatever the options. */
  already_paid?: boolean;
}

/**
 * Backend pre-calculated numbers for an export selection: how many profiles
 * resolve, how many already have revealed contacts (not re-charged), and the
 * per-unit credit costs. The wizard multiplies these client-side as the user
 * toggles format/contacts so the displayed price matches what the backend
 * charges.
 *
 * `insights` is hand-typed until the orval client is regenerated against the
 * backend that ships the insights export.
 */
export type ExportEstimate = ExportEstimateSchema & {
  insights?: InsightsExportEstimate | null;
};

export function useExportEstimate(
  source: ExportSource | null,
  { staleTime = 30_000 }: { staleTime?: number } = {},
) {
  return useQuery({
    queryKey: ["export-estimate", source],
    queryFn: () =>
      customInstance<ExportEstimate>(`/api/exports/estimate`, {
        method: "POST",
        body: JSON.stringify({ source }),
      }),
    enabled: !!source,
    staleTime,
  });
}

/** `POST /api/exports`; hand it to the export tracker as its `post`. */
export function requestExport({
  source,
  format = "pdf",
  include_contacts = false,
  options,
  hidden_insight_sections,
  expected_credits,
}: StartExportParams) {
  return customInstance<{ job_id: string }>(`/api/exports`, {
    method: "POST",
    body: JSON.stringify({
      source,
      format,
      include_contacts,
      name_redaction: options?.name_redaction ?? "none",
      hide_exporter: options?.hide_exporter ?? false,
      verified_contacts_only: options?.verified_contacts_only ?? false,
      personal_emails_only: options?.personal_emails_only ?? false,
      personal_emails_preferred: options?.personal_emails_preferred ?? false,
      include_highlights: options?.include_highlights ?? true,
      csv_columns: options?.csv_columns ?? null,
      theme: options?.theme ?? "dark",
      // Omitted from the JSON when undefined.
      hidden_insight_sections,
      expected_credits,
    }),
  });
}

/**
 * Sourced from the orval-generated schema so the shape stays in sync with the
 * backend OpenAPI spec (regenerate via `npm run generate`). `useExportHistory`
 * below just layers polling on top of the same payload.
 *
 * `source_type` is hand-typed until the backend history schema exposes it and
 * the orval client is regenerated; while absent, no source badge renders.
 */
export type ExportHistoryItem = ExportHistoryItemSchema & {
  source_type?: string | null;
};
export type ExportHistoryList = ExportHistoryListSchema;

export function useExportHistory(page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ["export-history", page, perPage],
    queryFn: () =>
      customInstance<ExportHistoryList>(
        `/api/exports/history?page=${page}&per_page=${perPage}`,
      ),
    staleTime: 5_000,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const hasInProgress = query.state.data?.exports?.some(
        (e) => e.status === "processing" || e.status === "pending",
      );
      return hasInProgress ? 3000 : false;
    },
  });
}
